/**
 * Maps the previous program's work order tables (munkalapok.csv,
 * munkalap_tetelek.csv from tools/migration) to closed work orders.
 * Vehicles are found by their old id, else by plate (merged duplicates),
 * customers by their old id, else the vehicle's owner.
 */
import { plateSearchKey } from "./masterData";
import type { Row } from "./legacyImport";

export const LEGACY_WORK_ORDER_ITEM_COLUMNS = ["munkalap_id", "tipus", "megnevezes", "mennyiseg", "brutto_ertek", "netto_ertek"] as const;

export interface LegacyLookup {
  vehicleByLegacyId: ReadonlyMap<string, string>;
  /** plateSearchKey → vehicles with that plate (id + owner). */
  vehiclesByPlate: ReadonlyMap<string, ReadonlyArray<{ id: string; customerId: string | null }>>;
  customerByLegacyId: ReadonlyMap<string, string>;
  ownerOfVehicle: ReadonlyMap<string, string | null>;
}

export interface LegacyWorkOrder {
  legacyId: string;
  number: string;
  vehicleId: string;
  customerId: string;
  receivedAt: string;
  needsReview: string | null;
}

export interface LegacyWorkOrderItem {
  workOrderLegacyId: string;
  legacyId: string;
  sortOrder: number;
  kind: "part" | "labour" | "other";
  partNumber: string | null;
  description: string;
  unit: string | null;
  quantity: number;
  unitPrice: number;
  priceMode: "gross";
  vatRate: number;
  net: number;
  vat: number;
  gross: number;
}

export interface LegacyWorkOrderPlan {
  orders: LegacyWorkOrder[];
  items: LegacyWorkOrderItem[];
  /** Work orders whose vehicle or customer could not be found. */
  skippedOrders: number;
  /** Lines with zero quantity in the old program (left out). */
  skippedItems: number;
  /** Work orders to check later (e.g. several vehicles with the same plate). */
  toReview: number;
}

const s = (v: string | undefined) => (v ?? "").trim();
const num = (v: string | undefined) => {
  const n = Number(s(v).replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};
const id = (v: string | undefined) => s(v).replace(/\.0$/, "");

export function planLegacyWorkOrders(orderRows: readonly Row[], itemRows: readonly Row[], look: LegacyLookup): LegacyWorkOrderPlan {
  const orders: LegacyWorkOrder[] = [];
  let skippedOrders = 0;
  let toReview = 0;
  for (const r of orderRows) {
    const legacyId = id(r.id);
    if (!legacyId) {
      skippedOrders++;
      continue;
    }
    const customerLegacy = id(r.ugyfel_id);
    let review = s(r.ellenorzes_oka) || null;
    let vehicleId = look.vehicleByLegacyId.get(id(r.jarmu_id)) ?? null;
    if (!vehicleId) {
      const candidates = look.vehiclesByPlate.get(plateSearchKey(s(r.rendszam))) ?? [];
      const ownerId = look.customerByLegacyId.get(customerLegacy) ?? null;
      const match = candidates.find((v) => ownerId && v.customerId === ownerId) ?? candidates[0];
      vehicleId = match?.id ?? null;
      if (match && candidates.length > 1 && !candidates.some((v) => ownerId && v.customerId === ownerId)) review = review ?? "plate";
    }
    const customerId = look.customerByLegacyId.get(customerLegacy) ?? (vehicleId ? look.ownerOfVehicle.get(vehicleId) ?? null : null);
    if (!vehicleId || !customerId) {
      skippedOrders++;
      continue;
    }
    if (review) toReview++;
    const date = s(r.datum);
    orders.push({
      legacyId,
      number: s(r.munkalapszam) || `R-${legacyId}`,
      vehicleId,
      customerId,
      receivedAt: /^\d{4}-\d{2}-\d{2}/.test(date) ? `${date.slice(0, 10)}T08:00:00Z` : new Date(0).toISOString(),
      needsReview: review,
    });
  }

  const known = new Set(orders.map((o) => o.legacyId));
  const items: LegacyWorkOrderItem[] = [];
  let skippedItems = 0;
  for (const r of itemRows) {
    const orderId = id(r.munkalap_id);
    if (!known.has(orderId)) continue;
    const quantity = Math.round(num(r.mennyiseg) * 1000) / 1000;
    const net = Math.round(num(r.netto_ertek));
    const gross = Math.round(num(r.brutto_ertek));
    const description = s(r.megnevezes);
    if (!(quantity > 0) || !Number.isFinite(net) || !Number.isFinite(gross) || !description) {
      skippedItems++;
      continue;
    }
    const kind = s(r.tipus) === "munkadij" ? "labour" : s(r.tipus) === "alkatresz" ? "part" : "other";
    const unitGross = num(r.brutto_egysegar);
    items.push({
      workOrderLegacyId: orderId,
      legacyId: id(r.id) || `${orderId}-${s(r.sor)}`,
      sortOrder: Number(s(r.sor)) || 0,
      kind,
      partNumber: s(r.cikkszam) || null,
      description,
      unit: s(r.mertekegyseg) || null,
      quantity,
      unitPrice: Number.isFinite(unitGross) ? Math.round(unitGross) : Math.round(gross / quantity),
      priceMode: "gross",
      vatRate: Number.isFinite(num(r.afa_kulcs)) ? num(r.afa_kulcs) : 27,
      // The old program's line totals are kept as they were invoiced.
      net,
      vat: gross - net,
      gross,
    });
  }
  return { orders, items, skippedOrders, skippedItems, toReview };
}

/** Readings above this are typing errors in the old program (e.g. 4 800 000). */
export const MAX_PLAUSIBLE_KM = 2_000_000;

export interface LegacyWorkOrderExtra {
  number: string;
  odometerKm: number | null;
  notes: string | null;
}

export interface LegacyWorkOrderExtrasPlan {
  rows: LegacyWorkOrderExtra[];
  withKm: number;
  withNotes: number;
  /** Readings left out as implausible. */
  implausibleKm: number;
}

/**
 * Odometer readings and remarks of the old work orders, from the old program's
 * work order list (munkalap_kiegeszites.csv: munkalapszam, km_ora, megjegyzes).
 * 0 km means "not recorded" in the old program.
 */
export function planLegacyWorkOrderExtras(rows: readonly Row[]): LegacyWorkOrderExtrasPlan {
  const out: LegacyWorkOrderExtra[] = [];
  let implausibleKm = 0;
  for (const r of rows) {
    const number = s(r.munkalapszam);
    if (!number) continue;
    const km = Math.round(num(r.km_ora));
    let odometerKm: number | null = Number.isFinite(km) && km > 0 ? km : null;
    if (odometerKm !== null && odometerKm > MAX_PLAUSIBLE_KM) {
      implausibleKm++;
      odometerKm = null;
    }
    const notes = s(r.megjegyzes) || null;
    if (odometerKm === null && notes === null) continue;
    out.push({ number, odometerKm, notes });
  }
  return {
    rows: out,
    withKm: out.filter((r) => r.odometerKm !== null).length,
    withNotes: out.filter((r) => r.notes !== null).length,
    implausibleKm,
  };
}
